import { Link, useSearchParams } from "react-router-dom";
import { Plus, Filter, Search, Grid3x3, List, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { API_BASE } from "@/lib/utils";
import { resolveMediaUrl } from "@/lib/media-api";
import { useAdminEvents } from "@/contexts/AdminEventsContext";

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

function normalizeEvent(row = {}) {
  return {
    id: String(row.id || ""),
    slug: row.slug || "",
    title: row.title || "Untitled event",
    subtitle: row.subtitle || "",
    venue: row.venue || "TBD",
    startDate: row.startDate || "",
    status: row.status || "Draft",
    accent: row.accent || "var(--primary)",
    cover: row.cover || "",
    registered: Number(row.registered || 0),
    capacity: Math.max(1, Number(row.capacity || 0)),
    revenue: Number(row.revenue || 0),
    isSandboxDemo: Boolean(row.isSandboxDemo),
  };
}

function DeleteEventDialog({ open, onClose, event, onConfirm, busy }) {
  const [typed, setTyped] = useState("");
  const confirmWord = "DELETE";

  useEffect(() => {
    if (open) setTyped("");
  }, [open, event?.id]);

  if (!open || !event) return null;
  const canDelete = typed.trim() === confirmWord;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true">
      <div className="surface w-full max-w-md border border-[#EF4444]/40 p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl text-[#EF4444]">Delete event</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-2">
              Permanently remove <span className="font-medium text-[var(--text-primary)]">{event.title}</span> and all
              associated attendees, campaigns, and settings. This cannot be undone.
            </p>
          </div>
          <button type="button" className="btn-ghost p-2 cursor-pointer" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <label className="block mt-5 text-xs text-[var(--text-secondary)]">
          Type <span className="font-mono text-[var(--text-primary)]">{confirmWord}</span> to enable delete
          <input
            className="input-dark w-full mt-2 font-mono"
            value={typed}
            onChange={(ev) => setTyped(ev.target.value)}
            placeholder={confirmWord}
            autoComplete="off"
            data-testid="delete-event-confirm-input"
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-ghost cursor-pointer" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary bg-[#EF4444] hover:bg-[#DC2626] border-[#EF4444] disabled:opacity-40 cursor-pointer"
            disabled={!canDelete || busy}
            onClick={onConfirm}
            data-testid="delete-event-confirm-btn"
          >
            {busy ? "Deleting…" : "Delete event"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EventsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") === "list" ? "list" : "grid";
  const q = searchParams.get("q") || "";
  const statusFilter = searchParams.get("status") || "all";
  const regionFilter = searchParams.get("region") || "all";
  const ownerFilter = searchParams.get("owner") || "all";

  const patchParams = (patch) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        Object.entries(patch).forEach(([key, value]) => {
          const omit =
            value === undefined ||
            value === null ||
            value === "" ||
            value === "all" ||
            (key === "view" && value === "grid");
          if (omit) p.delete(key);
          else p.set(key, String(value));
        });
        return p;
      },
      { replace: true },
    );
  };
  const { events: layoutEvents, setEvents: setLayoutEvents, eventsLoading, regions, refreshEvents } = useAdminEvents();
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    refreshEvents();
  }, [refreshEvents]);

  const events = useMemo(
    () => (Array.isArray(layoutEvents) ? layoutEvents.map(normalizeEvent).filter((e) => e.id) : []),
    [layoutEvents],
  );
  const loading = eventsLoading;

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return events.filter((e) => {
      const region = deriveRegion(e.venue);
      const owner = deriveOwner(e);
      const statusOk = statusFilter === "all" || String(e.status || "").toLowerCase() === statusFilter;
      const regionOk = regionFilter === "all" || region.toLowerCase() === regionFilter;
      const ownerOk = ownerFilter === "all" || owner.toLowerCase() === ownerFilter;
      const searchOk = !term || `${e.title} ${e.subtitle} ${e.venue} ${e.slug} ${e.status} ${owner}`.toLowerCase().includes(term);
      return statusOk && regionOk && ownerOk && searchOk;
    });
  }, [events, q, statusFilter, regionFilter, ownerFilter]);

  const statusOptions = useMemo(
    () => ["all", ...new Set(events.map((e) => String(e.status || "Draft").toLowerCase()))],
    [events],
  );
  const regionOptions = useMemo(
    () => ["all", ...new Set(events.map((e) => deriveRegion(e.venue).toLowerCase()))],
    [events],
  );
  const ownerOptions = useMemo(
    () => ["all", ...new Set(events.map((e) => deriveOwner(e).toLowerCase()))],
    [events],
  );
  const deleteEvent = (eventRow) => {
    setDeleteTarget(eventRow);
  };

  const confirmDeleteEvent = async () => {
    if (!deleteTarget) return;
    const eventRow = deleteTarget;
    try {
      setDeletingId(eventRow.id);
      const res = await fetch(`${API_BASE}/api/admin/events/${encodeURIComponent(eventRow.id)}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setLayoutEvents((prev) => (Array.isArray(prev) ? prev.filter((e) => String(e.id) !== eventRow.id) : []));
      setDeleteTarget(null);
      toast.success("Event deleted", { description: eventRow.title });
    } catch (err) {
      toast.error("Delete failed", { description: err?.message || "Could not delete event." });
    } finally {
      setDeletingId("");
    }
  };
  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1500px] mx-auto" data-testid="events-list">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-overline">All events</div>
          <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Portfolio</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2">
            {loading ? "Loading events..." : `${filtered.length} events · spanning ${regions} regions`}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/events/new" className="btn-primary" data-testid="events-new-btn">
            <Plus size={14} /> New event
          </Link>
        </div>
      </div>

      <div className="mt-10 border border-[var(--border-default)] bg-[var(--bg-surface)] p-2 flex flex-col gap-2 xl:flex-row xl:items-center xl:flex-wrap">
        <div className="flex items-center gap-2 min-w-0 xl:flex-1 xl:min-w-[16rem]">
          <Search size={14} className="ml-2 shrink-0 text-[var(--text-tertiary)]" />
          <input
            value={q}
            onChange={(e) => patchParams({ q: e.target.value })}
            placeholder="Search by title, venue, slug…"
            className="bg-transparent min-w-0 flex-1 px-2 py-1.5 text-sm focus:outline-none"
            data-testid="events-search"
          />
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-2 xl:shrink-0">
          <label className="inline-flex items-center gap-2 text-xs font-mono text-[var(--text-secondary)] px-2 shrink-0">
            <Filter size={12} /> Status
            <select
              value={statusFilter}
              onChange={(e) => patchParams({ status: e.target.value })}
              className="bg-[var(--bg-elev)] border border-[var(--border-default)] px-2 py-1 max-w-[9rem]"
            >
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s === "all" ? "All" : titleCase(s)}
                </option>
              ))}
            </select>
          </label>
          <label className="inline-flex items-center gap-2 text-xs font-mono text-[var(--text-secondary)] px-2 shrink-0">
            Region
            <select
              value={regionFilter}
              onChange={(e) => patchParams({ region: e.target.value })}
              className="bg-[var(--bg-elev)] border border-[var(--border-default)] px-2 py-1 max-w-[9rem]"
            >
              {regionOptions.map((s) => (
                <option key={s} value={s}>
                  {s === "all" ? "All" : titleCase(s)}
                </option>
              ))}
            </select>
          </label>
          <label className="inline-flex items-center gap-2 text-xs font-mono text-[var(--text-secondary)] px-2 shrink-0">
            Owner
            <select
              value={ownerFilter}
              onChange={(e) => patchParams({ owner: e.target.value })}
              className="bg-[var(--bg-elev)] border border-[var(--border-default)] px-2 py-1 max-w-[9rem]"
            >
              {ownerOptions.map((s) => (
                <option key={s} value={s}>
                  {s === "all" ? "All" : titleCase(s)}
                </option>
              ))}
            </select>
          </label>
          <div className="border-l border-[var(--border-default)] pl-2 ml-auto flex shrink-0">
            <button type="button" onClick={() => patchParams({ view: "grid" })} className={`p-2 ${view === "grid" ? "text-[var(--primary)]" : "text-[var(--text-tertiary)]"}`} data-testid="view-grid"><Grid3x3 size={14} /></button>
            <button type="button" onClick={() => patchParams({ view: "list" })} className={`p-2 ${view === "list" ? "text-[var(--primary)]" : "text-[var(--text-tertiary)]"}`} data-testid="view-list"><List size={14} /></button>
          </div>
        </div>
      </div>

      {(q || statusFilter !== "all" || regionFilter !== "all" || ownerFilter !== "all") && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="events-filter-chips">
          <span className="text-[10px] font-mono text-[var(--text-tertiary)] uppercase tracking-widest">Active</span>
          {q ? (
            <button type="button" className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 border border-[var(--border-default)] bg-[var(--bg-elev)] hover:border-[color-mix(in_srgb,var(--primary)_50%,transparent)]" onClick={() => patchParams({ q: "" })}>
              Search: {q} <X size={12} />
            </button>
          ) : null}
          {statusFilter !== "all" ? (
            <button type="button" className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 border border-[var(--border-default)] bg-[var(--bg-elev)] hover:border-[color-mix(in_srgb,var(--primary)_50%,transparent)]" onClick={() => patchParams({ status: "all" })}>
              Status: {titleCase(statusFilter)} <X size={12} />
            </button>
          ) : null}
          {regionFilter !== "all" ? (
            <button type="button" className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 border border-[var(--border-default)] bg-[var(--bg-elev)] hover:border-[color-mix(in_srgb,var(--primary)_50%,transparent)]" onClick={() => patchParams({ region: "all" })}>
              Region: {titleCase(regionFilter)} <X size={12} />
            </button>
          ) : null}
          {ownerFilter !== "all" ? (
            <button type="button" className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 border border-[var(--border-default)] bg-[var(--bg-elev)] hover:border-[color-mix(in_srgb,var(--primary)_50%,transparent)]" onClick={() => patchParams({ owner: "all" })}>
              Owner: {titleCase(ownerFilter)} <X size={12} />
            </button>
          ) : null}
          <button type="button" className="text-[10px] font-mono text-[var(--text-tertiary)] hover:text-[var(--text-primary)] ml-2" onClick={() => setSearchParams({}, { replace: true })}>
            Clear all
          </button>
        </div>
      )}

      {view === "grid" ? (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((e, i) => {
            const coverUrl = resolveMediaUrl(e.cover);
            return (
            <Link to={`/admin/events/${e.id}`} key={e.id} className="surface surface-hover fade-up block relative" style={{ animationDelay: `${i * 60}ms` }}>
              {!e.isSandboxDemo ? (
              <button
                type="button"
                className="absolute top-3 right-3 z-10 border border-[var(--error)]/40 text-[var(--error)] bg-[var(--bg-surface)]/80 px-2 py-1 text-[10px] font-mono uppercase tracking-widest"
                onClick={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  deleteEvent(e);
                }}
                disabled={deletingId === e.id}
              >
                <Trash2 size={11} className="inline mr-1" />
                {deletingId === e.id ? "Deleting..." : "Delete"}
              </button>
              ) : null}
              <div className="aspect-video relative overflow-hidden bg-[var(--bg-elev)]">
                {coverUrl ? (
                  <img src={coverUrl} className="w-full h-full object-cover opacity-50" alt="" />
                ) : (
                  <div
                    className="w-full h-full opacity-40"
                    style={{ background: `linear-gradient(135deg, ${e.accent}55 0%, var(--bg-elev) 60%)` }}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-surface)] via-transparent to-transparent" />
                <span className="absolute top-3 left-3 text-[10px] font-mono uppercase px-2 py-1 border tracking-widest" style={{ color: e.accent, borderColor: e.accent + "55", background: "rgba(0,0,0,0.4)" }}>
                  {e.isSandboxDemo ? "Demo" : e.status}
                </span>
              </div>
              <div className="p-6">
                <div className="font-heading text-xl leading-tight">{e.title}</div>
                <div className="text-sm text-[var(--text-secondary)] mt-1">{e.subtitle}</div>
                <div className="text-xs text-[var(--text-tertiary)] font-mono mt-3">{e.venue} · {e.startDate}</div>
                <div className="mt-5 flex items-center justify-between text-xs">
                  <div className="font-mono">
                    <span className="text-[var(--text-primary)]">{e.registered.toLocaleString()}</span>
                    <span className="text-[var(--text-tertiary)]"> / {e.capacity.toLocaleString()}</span>
                    {e.isSandboxDemo ? <span className="ml-1 text-[var(--text-tertiary)]">(preview)</span> : null}
                  </div>
                  <div className="font-mono text-[var(--primary)]">{e.isSandboxDemo ? "—" : `$${(e.revenue / 1000).toFixed(0)}k`}</div>
                </div>
                <div className="mt-3 h-1 bg-[var(--bg-elev)] overflow-hidden">
                  <div
                    className="h-full max-w-full"
                    style={{
                      width: `${e.capacity > 0 ? Math.min(100, (e.registered / e.capacity) * 100) : 0}%`,
                      background: e.registered > e.capacity ? "var(--error)" : e.accent,
                    }}
                  />
                </div>
              </div>
            </Link>
          );
          })}
          {!loading && !error && filtered.length === 0 ? (
            <div className="text-sm text-[var(--text-secondary)]">
              {events.length === 0
                ? "No events yet. Create one with New event."
                : "No events match this search."}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="mt-6 surface">
          <table className="w-full text-sm">
            <thead className="border-b border-[var(--border-default)]">
              <tr className="text-left text-[var(--text-tertiary)] font-mono text-[11px] uppercase tracking-widest">
                <th className="p-4">Event</th>
                <th className="p-4">Status</th>
                <th className="p-4">Dates</th>
                <th className="p-4">Venue</th>
                <th className="p-4 text-right">Registered</th>
                <th className="p-4 text-right">Revenue</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} className="border-b border-[var(--border-default)] hover:bg-[var(--bg-elev)] transition">
                  <td className="p-4">
                    <Link to={`/admin/events/${e.id}`} className="hover:text-[var(--primary)] font-medium">{e.title}</Link>
                    <div className="text-xs text-[var(--text-tertiary)] font-mono">/{e.slug}</div>
                  </td>
                  <td className="p-4"><span className="font-mono text-[10px] px-2 py-1 border uppercase tracking-widest" style={{ color: e.accent, borderColor: e.accent + "55" }}>{e.status}</span></td>
                  <td className="p-4 font-mono text-xs">{e.startDate}</td>
                  <td className="p-4 text-[var(--text-secondary)]">{e.venue}</td>
                  <td className="p-4 text-right font-mono">{e.registered.toLocaleString()} / {e.capacity.toLocaleString()}</td>
                  <td className="p-4 text-right font-mono text-[var(--primary)]">${(e.revenue / 1000).toFixed(0)}k</td>
                  <td className="p-4 text-right">
                    <button
                      type="button"
                      className="text-[11px] font-mono uppercase tracking-widest border border-[var(--error)]/40 text-[var(--error)] px-2 py-1"
                      onClick={() => deleteEvent(e)}
                      disabled={deletingId === e.id}
                    >
                      {deletingId === e.id ? "Deleting..." : "Delete"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && !error && filtered.length === 0 ? (
            <div className="p-4 text-sm text-[var(--text-secondary)]">
              {events.length === 0
                ? "No events yet. Create one with New event."
                : "No events match this search."}
            </div>
          ) : null}
        </div>
      )}
      {error && <div className="mt-4 text-sm text-[var(--error)]">{error}</div>}
      <DeleteEventDialog
        open={Boolean(deleteTarget)}
        event={deleteTarget}
        busy={Boolean(deleteTarget && deletingId === deleteTarget.id)}
        onClose={() => {
          if (!deletingId) setDeleteTarget(null);
        }}
        onConfirm={confirmDeleteEvent}
      />
    </div>
  );
}

function deriveRegion(venue) {
  const raw = String(venue || "").trim();
  if (!raw) return "Unknown";
  const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
  return parts[parts.length - 1] || "Unknown";
}

function deriveOwner(event) {
  return String(event?.owner || "Unassigned").trim() || "Unassigned";
}

function titleCase(value) {
  return String(value || "")
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ""))
    .join(" ");
}
